import { FilterBar } from "@/components/FilterBar";
import { Footer } from "@/components/footer";
import { Header } from "@/components/header";
import { RoundKeyboardArrowLeft } from "@/components/icons/leftArrowIcon";
import {
  Button,
  Divider,
  HStack,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { MobileContent } from "./mobileContent";
import { DesktopContent } from "./desktopContent";
import {
  EventDetailsDto,
  GraduateDetailsDto,
  GraduateGuestDataDto,
  TransferGraduateEntranceDto,
} from "../types";
import Joi from "joi";
import { useEvent } from "@/hooks/useEvent";
import { useCallback, useEffect, useState } from "react";
import LoadingOverlay from "@/components/overlay";
import { useForm } from "react-hook-form";
import { useCustomToast } from "@/hooks/useToast";
import { joiResolver } from "@hookform/resolvers/joi";

export type GuestFormData = {
  entranceId: number;
  name: string;
  email: string;
  phone: string;
};

const schema = Joi.object({
  entranceId: Joi.alternatives()
    .try(Joi.number())
    .invalid("")
    .required()
    .messages({
      "any.required": "Tipo de Lote deve ser Selecionado",
      "any.invalid": "Tipo de Lote deve ser Selecionado",
    }),
  name: Joi.string().required().messages({
    "string.empty": "Nome deve ser informado",
  }),
  email: Joi.string().required().messages({
    "string.empty": "E-mail deve ser informado",
  }),
  phone: Joi.string().required().messages({
    "string.empty": "Telefone deve ser informado",
  }),
});

export const InvitationDistribuition = () => {
  const navigate = useNavigate();
  const isTablet = useBreakpointValue({ base: true, md: true, lg: false });
  const { id: graduateId } = useParams<{ id: string }>();
  const eventGraduateId = Number(graduateId);
  const {
    getGraduateDetails,
    getGraduateByEvent,
    createGraduateGuest,
    updateGraduateGuest,
    transferGraduateEntrance,
    sendInviteMail,
    donwloadInvite,
    deleteGuest,
  } = useEvent();
  const [graduates, setGraduates] = useState<GraduateDetailsDto>();
  const [event, setEvent] = useState<EventDetailsDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [guestEdit, setGuestEdit] = useState<GraduateGuestDataDto | null>(null);
  const { customToast } = useCustomToast();
  const [query, setQuery] = useState("");
  const { isOpen: isCollapseOpen, onToggle: onCollapseToggle } =
    useDisclosure();
  const {
    isOpen: isTransferModal,
    onOpen: onOpenTransferModal,
    onClose: onCloseTransfer,
  } = useDisclosure();
  const {
    isOpen: isOpenAddInvite,
    onOpen: onOpenAddInvite,
    onClose: onCloseAddInvite,
  } = useDisclosure();
  const filtered = event.filter((item) =>
    item.name.toLowerCase().includes(query.toLowerCase())
  );

  const callbackGraduates = useCallback(() => {
    if (!graduateId) return;
    setIsLoading(true);
    getGraduateDetails(Number(graduateId))
      .then((response) => {
        setGraduates(response.data);
        const eventId = response.data.event.id;
        return getGraduateByEvent(eventId);
      })
      .then((event) => {
        setEvent(event.data);
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    callbackGraduates();
  }, [callbackGraduates]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm<GuestFormData>({
    resolver: joiResolver(schema),
  });

  const {
    register: registerTransfer,
    handleSubmit: handleSubmitTrasnfer,
    reset: resetTransfer,
    setValue: setValueTransfer,
  } = useForm<TransferGraduateEntranceDto>();

  const onSubmitTransfer = (data: TransferGraduateEntranceDto) => {
    const payload = {
      fromGraduateId: data.fromGraduateId,
      toGraduateId: data.toGraduateId,
      entranceId: Number(data.entranceId),
      quantity: Number(data.quantity),
    };
    setIsLoading(true);
    transferGraduateEntrance(payload)
      .then(() => {
        customToast("success", "Lote Transferido com sucesso!");
      })
      .catch((error) => {
        if (!payload.toGraduateId) {
          customToast("error", "Um formando deve ser seleciado.");
        } else {
          customToast("error", error.response?.data?.message);
        }
      })
      .finally(() => {
        setQuery("");
        callbackGraduates();
        onCloseTransfer();
        resetTransfer({
          toGraduateId: undefined,
          entranceId: undefined,
          quantity: undefined,
        });
        setIsLoading(false);
      });
  };

  const handleCancelTransfer = () => {
    setQuery("");
    resetTransfer({
      toGraduateId: undefined,
      entranceId: undefined,
      quantity: undefined,
    });
    onCloseTransfer();
  };

  const onSubmit = (data: GuestFormData) => {
    const payload = {
      entranceId: Number(data.entranceId),
      name: data.name,
      email: data.email,
      phone: cleanPhoneMask(data.phone),
      wasSent: false,
      isPresent: false,
    };
    setIsLoading(true);
    const promise = guestEdit
      ? updateGraduateGuest(guestEdit.id, payload)
      : createGraduateGuest({
          ...payload,
          eventGraduateId: eventGraduateId,
        });
    promise
      .then(() => {
        customToast("success", "Convidado Cadastrado com Sucesso");
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        callbackGraduates();
        onCloseAddInvite();
        reset();
        setIsLoading(false);
      });
  };

  const handleEditGuest = (guest: GraduateGuestDataDto) => {
    setGuestEdit(guest);
    reset({
      entranceId: guest.entrance.id,
      name: guest.name,
      email: guest.email,
      phone: phoneMask(guest.phone),
    });
    onOpenAddInvite();
  };

  const handleNewGuest = () => {
    setGuestEdit(null);
    reset({
      entranceId: undefined,
      name: "",
      email: "",
      phone: "",
    });
    onOpenAddInvite();
  };

  const handleSendEmail = (id: number) => {
    setIsLoading(true);
    sendInviteMail(id)
      .then(() => {
        customToast("success", "E-mail enviado com sucesso");
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        callbackGraduates();
        setIsLoading(false);
      });
  };

  const handleDownloadInvite = (id: number) => {
    setIsLoading(true);
    donwloadInvite(id)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `convite_${id}.png`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        customToast("success", "Convite baixado!");
      })
      .catch(() => {
        if (!graduates?.event.situation) {
          customToast("error", "Evento Cancelado");
        } else {
          customToast("error", "Erro ao baixar convite");
        }
      })
      .finally(() => {
        callbackGraduates();
        setIsLoading(false);
      });
  };

  const handleDeleteGuest = (id: number) => {
    setIsLoading(true);
    deleteGuest(id)
      .then(() => {
        customToast("success", "Convidado deletado com sucesso");
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        callbackGraduates();
        setIsLoading(false);
      });
  };

  const phoneMask = (value: string) => {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4,5})(\d{4})/, "$1-$2")
      .replace(/(-\d{4})\d+?$/, "$1");
  };

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const maskedValue = phoneMask(e.target.value);
    onChange(maskedValue);
  };

  const cleanPhoneMask = (value: string) => {
    return value.replace(/\D/g, "");
  };
  return (
    <Stack
      w="100%"
      position="relative"
      gap={0}
      h={event.length < 4 ? "100vh" : "100%"}
    >
      <Header />
      <HStack bgColor="neutral.300">
        <Button
          variant="ghost"
          mb={4}
          ml={3}
          color="secondary.500"
          _hover={{ bgColor: "neutral.300", color: "secondary.300" }}
          onClick={() => navigate("/eventos")}
        >
          <RoundKeyboardArrowLeft />
          <Text fontSize="20px">Voltar</Text>
        </Button>
        <Divider orientation="vertical" height="60px" mx={3} />
        <FilterBar title="Distribuição de Convites" columns={3} />
      </HStack>
      {isLoading && <LoadingOverlay />}
      {isTablet ? (
        <MobileContent
          events={event}
          register={register}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          errors={errors}
          page={1}
          lastPage={1}
          setPage={() => null}
          graduates={graduates!}
          onCollapseToggle={onCollapseToggle}
          isCollapseOpen={isCollapseOpen}
          isTransferModal={isTransferModal}
          onOpenTransferModal={onOpenTransferModal}
          onCloseTransfer={onCloseTransfer}
          isOpenAddInvite={isOpenAddInvite}
          onCloseAddInvite={onCloseAddInvite}
          handleEditGuest={handleEditGuest}
          handleNewGuest={handleNewGuest}
          registerTransfer={registerTransfer}
          handleSubmitTrasnfer={handleSubmitTrasnfer}
          onSubmitTransfer={onSubmitTransfer}
          setValueTransfer={setValueTransfer}
          handleSendEmail={handleSendEmail}
          handleDownloadInvite={handleDownloadInvite}
          query={query}
          setQuery={setQuery}
          filtered={filtered}
          handleCancelTransfer={handleCancelTransfer}
          handleDeleteGuest={handleDeleteGuest}
          handlePhoneChange={handlePhoneChange}
          phoneMask={phoneMask}
          control={control}
        />
      ) : (
        <DesktopContent
          graduates={graduates!}
          register={register}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          errors={errors}
          onCollapseToggle={onCollapseToggle}
          isCollapseOpen={isCollapseOpen}
          isTransferModal={isTransferModal}
          onOpenTransferModal={onOpenTransferModal}
          onCloseTransfer={onCloseTransfer}
          isOpenAddInvite={isOpenAddInvite}
          onCloseAddInvite={onCloseAddInvite}
          handleEditGuest={handleEditGuest}
          handleNewGuest={handleNewGuest}
          registerTransfer={registerTransfer}
          handleSubmitTrasnfer={handleSubmitTrasnfer}
          onSubmitTransfer={onSubmitTransfer}
          setValueTransfer={setValueTransfer}
          handleSendEmail={handleSendEmail}
          handleDownloadInvite={handleDownloadInvite}
          query={query}
          setQuery={setQuery}
          filtered={filtered}
          handleCancelTransfer={handleCancelTransfer}
          handleDeleteGuest={handleDeleteGuest}
          handlePhoneChange={handlePhoneChange}
          phoneMask={phoneMask}
          control={control}
        />
      )}
      {!isTablet && <Footer />}
    </Stack>
  );
};
